import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import Lenis from "lenis";
import { setLenis, getLenis, scrollToId } from "@/lib/scroll";
import Globe3D from "@/components/Globe3D";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Manifesto from "@/components/Manifesto";
import SiteIndex from "@/components/SiteIndex";
import SiteDrawer from "@/components/SiteDrawer";
import RoutePanel from "@/components/RoutePanel";
import TourOverlay from "@/components/TourOverlay";
import CompareView from "@/components/CompareView";
import ComparePill from "@/components/ComparePill";
import PosterModal from "@/components/PosterModal";
import InstallButton from "@/components/InstallButton";
import ReviewPage from "@/pages/ReviewPage";
import Footer from "@/components/Footer";
import { SITES } from "@/data/sites";
import { EPOCH_START } from "@/data/enhancements";
import { getNotes, onNotesChange } from "@/lib/notes";
import { fetchOrigins } from "@/lib/theories";
import OfflineBanner from "@/components/OfflineBanner";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: false };
  }
  static getDerivedStateFromError() {
    return { err: true };
  }
  render() {
    if (this.state.err) {
      return (
        <div className="flex h-screen items-center justify-center bg-[#0B0C10] font-mono text-sm text-white/60">
          Something broke mid-expedition — please reload.
        </div>
      );
    }
    return this.props.children;
  }
}

const Landing = () => {
  const globeRef = useRef(null);
  const telemetryRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const [epochYear, setEpochYear] = useState(null);
  const [routeIds, setRouteIds] = useState([]);
  const [routeOpen, setRouteOpen] = useState(false);
  const [tourStops, setTourStops] = useState(null);
  const [tourId, setTourId] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [posterOpen, setPosterOpen] = useState(false);
  const [notedIds, setNotedIds] = useState(() => new Set(Object.keys(getNotes())));
  const [origins, setOrigins] = useState([]);
  const selected = SITES.find((s) => s.id === selectedId) || null;
  const comparePair = useMemo(
    () => compareIds.map((id) => SITES.find((s) => s.id === id)).filter(Boolean),
    [compareIds]
  );

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    setLenis(lenis);
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      setLenis(null);
    };
  }, []);

  useEffect(() => onNotesChange((all) => setNotedIds(new Set(Object.keys(all)))), []);

  /* where today's votes come from — refreshed every minute */
  useEffect(() => {
    let live = true;
    const load = () => fetchOrigins().then((o) => live && setOrigins(o)).catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    const l = getLenis();
    if (selectedId || tourStops || compareOpen || posterOpen) {
      l?.stop();
    } else {
      l?.start();
    }
  }, [selectedId, tourStops, compareOpen, posterOpen]);

  const startTour = useCallback(() => {
    const stops = routeIds.map((id) => SITES.find((s) => s.id === id)).filter(Boolean);
    if (!stops.length) return;
    setSelectedId(null);
    getLenis()?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    setTourStops(stops);
  }, [routeIds]);

  const exitTour = useCallback(() => {
    setTourStops(null);
    setTourId(null);
  }, []);

  const handleTourStop = useCallback((site) => {
    setTourId(site.id);
    globeRef.current?.flyTo?.(site);
  }, []);

  /* restore expedition route from share link or localStorage; auto-start tour / compare deep links.
     Runs on mount and again whenever the hash changes (shared links pasted into the same tab). */
  useEffect(() => {
    const applyHash = (fromEvent) => {
      try {
        const hash = window.location.hash;
        const m = hash.match(/route=([A-Za-z,-]+)/);
        const fromHash = m
          ? m[1].split(",").filter((id) => SITES.some((s) => s.id === id))
          : null;
        const stored = fromEvent ? null : JSON.parse(localStorage.getItem("aetheria-route") || "null");
        const initial =
          fromHash ||
          (Array.isArray(stored)
            ? stored.filter((id) => SITES.some((s) => s.id === id))
            : []);
        const wantsTour = !!fromHash && /(^#|&)tour=1/.test(hash);
        const c = hash.match(/compare=([A-Za-z-]+),([A-Za-z-]+)/);
        const compare = c ? [c[1], c[2]].filter((id) => SITES.some((s) => s.id === id)) : [];
        const wantsCompare = compare.length === 2 && compare[0] !== compare[1];

        if (initial.length) {
          setRouteIds(initial);
          if (wantsTour) {
            setSelectedId(null);
            setCompareOpen(false);
            getLenis()?.scrollTo(0, { immediate: true });
            window.scrollTo(0, 0);
            setTourStops(initial.map((id) => SITES.find((s) => s.id === id)));
          } else if (!fromEvent) {
            setRouteOpen(true);
          }
        }
        if (fromEvent && !wantsTour) setTourStops(null);
        if (wantsCompare) {
          setCompareIds(compare);
          setSelectedId(null);
          setCompareOpen(true);
        } else if (fromEvent) {
          setCompareOpen(false);
        }
      } catch {
        /* ignore */
      }
    };
    applyHash(false);
    const onHash = () => applyHash(true);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    localStorage.setItem("aetheria-route", JSON.stringify(routeIds));
    const parts = [];
    if (routeIds.length) parts.push(`route=${routeIds.join(",")}`);
    if (tourStops && routeIds.length) parts.push("tour=1");
    if (compareOpen && compareIds.length === 2) parts.push(`compare=${compareIds.join(",")}`);
    const next = parts.length
      ? `#${parts.join("&")}`
      : window.location.pathname + window.location.search;
    window.history.replaceState(null, "", next);
  }, [routeIds, tourStops, compareOpen, compareIds]);

  const handleSelect = useCallback((site) => {
    setSelectedId(site.id);
    globeRef.current?.flyTo?.(site);
  }, []);

  const handleTelemetry = useCallback((lat, lng) => {
    if (telemetryRef.current) {
      telemetryRef.current.textContent = `LAT ${lat >= 0 ? "+" : "−"}${Math.abs(lat)
        .toFixed(2)
        .padStart(5, "0")} · LNG ${lng >= 0 ? "+" : "−"}${Math.abs(lng)
        .toFixed(2)
        .padStart(5, "0")}`;
    }
  }, []);

  const handleRandom = useCallback(() => {
    const pick = SITES[Math.floor(Math.random() * SITES.length)];
    handleSelect(pick);
  }, [handleSelect]);

  const toggleRoute = useCallback((id) => {
    setRouteIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const toggleCompare = useCallback(
    (id) => {
      if (compareIds.includes(id)) {
        setCompareIds(compareIds.filter((x) => x !== id));
        return;
      }
      const next = compareIds.length < 2 ? [...compareIds, id] : [compareIds[1], id];
      setCompareIds(next);
      if (next.length === 1) {
        toast("Pinned for comparison — pin one more site", { duration: 2500 });
      } else {
        setSelectedId(null);
        setCompareOpen(true);
      }
    },
    [compareIds]
  );

  const openFromCompare = useCallback(
    (site) => {
      setCompareOpen(false);
      handleSelect(site);
    },
    [handleSelect]
  );

  const visibleIds = useMemo(
    () =>
      epochYear == null
        ? null
        : new Set(
            SITES.filter((s) => (EPOCH_START[s.id] ?? 0) >= epochYear).map((s) => s.id)
          ),
    [epochYear]
  );

  return (
    <div id="top" className="relative min-h-screen bg-[#0B0C10] text-[#F8FAFC]">
      <Globe3D
        ref={globeRef}
        sites={SITES}
        selectedId={selectedId}
        tourId={tourId}
        routeIds={routeIds}
        visibleIds={visibleIds}
        origins={origins}
        onSelect={handleSelect}
        onTelemetry={handleTelemetry}
      />

      <Header telemetryRef={telemetryRef} />
      <OfflineBanner />

      <main
        data-testid="main-content"
        className={`relative z-10 transition-opacity duration-700 ${
          tourStops ? "pointer-events-none opacity-0" : "opacity-100"
        }`}
        aria-hidden={!!tourStops}
      >
        <Hero
          onExplore={() => scrollToId("#index")}
          onRandom={handleRandom}
        />
        <Marquee />
        <Manifesto />
        <SiteIndex
          sites={SITES}
          activeId={selectedId}
          onSelect={handleSelect}
          epochYear={epochYear}
          onEpochChange={setEpochYear}
          routeIds={routeIds}
          onToggleRoute={toggleRoute}
          compareIds={compareIds}
          onToggleCompare={toggleCompare}
          notedIds={notedIds}
        />
        <Footer />
      </main>

      {!tourStops && (
        <>
          <div data-testid="bottom-dock" className="fixed bottom-5 left-5 z-40 flex flex-wrap items-center gap-2 sm:left-auto sm:right-5 sm:justify-end">
            <span className="sm:hidden">
              <InstallButton compact />
            </span>
            <ComparePill
              sites={SITES}
              compareIds={compareIds}
              onOpen={() => setCompareOpen(true)}
              onClear={() => setCompareIds([])}
            />
            <RoutePanel
              sites={SITES}
              routeIds={routeIds}
              open={routeOpen}
              setOpen={setRouteOpen}
              onRemove={toggleRoute}
              onClear={() => setRouteIds([])}
              onSelect={handleSelect}
              onStartTour={startTour}
              onPoster={() => setPosterOpen(true)}
            />
          </div>
        </>
      )}

      {tourStops && (
        <TourOverlay
          stops={tourStops}
          onStop={handleTourStop}
          onExit={exitTour}
          onOpenDossier={handleSelect}
          suspended={!!selectedId}
        />
      )}

      <AnimatePresence>
        {selected && (
          <SiteDrawer
            key={selected.id}
            site={selected}
            sites={SITES}
            onClose={() => setSelectedId(null)}
            onNav={handleSelect}
            inRoute={routeIds.includes(selected.id)}
            onToggleRoute={toggleRoute}
            inCompare={compareIds.includes(selected.id)}
            onToggleCompare={toggleCompare}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {compareOpen && comparePair.length === 2 && (
          <CompareView
            key="compare"
            pair={comparePair}
            onClose={() => setCompareOpen(false)}
            onRemove={(id) => {
              setCompareIds((prev) => prev.filter((x) => x !== id));
              setCompareOpen(false);
            }}
            onSwap={() => setCompareIds((prev) => [prev[1], prev[0]])}
            onOpen={openFromCompare}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {posterOpen && routeIds.length > 0 && (
          <PosterModal
            key="poster"
            stops={routeIds.map((id) => SITES.find((s) => s.id === id)).filter(Boolean)}
            onClose={() => setPosterOpen(false)}
          />
        )}
      </AnimatePresence>

      <Toaster position="bottom-center" theme="dark" />
    </div>
  );
};

function App() {
  const isReview = window.location.pathname.replace(/\/+$/, "") === "/review";
  return (
    <ErrorBoundary>
      {isReview ? <ReviewPage /> : <Landing />}
    </ErrorBoundary>
  );
}

export default App;
